import React, { useEffect, useRef, useId } from 'react'
import { createPortal } from 'react-dom'

interface ModalProps {
  isOpen: boolean
  onClose: () => void
  title?: string
  children: React.ReactNode
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full'
  showCloseButton?: boolean
  closeOnEscape?: boolean
  contentClassName?: string
  contentStyle?: React.CSSProperties
  overlayClassName?: string
  headerClassName?: string
  headerStyle?: React.CSSProperties
  titleClassName?: string
  bodyClassName?: string
  bodyStyle?: React.CSSProperties
  closeButtonClassName?: string
  closeButtonStyle?: React.CSSProperties
  footer?: React.ReactNode
  footerClassName?: string
  footerStyle?: React.CSSProperties
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  showCloseButton = true,
  closeOnEscape = true,
  contentClassName = '',
  contentStyle,
  overlayClassName = '',
  headerClassName = '',
  headerStyle,
  titleClassName = '',
  bodyClassName = '',
  bodyStyle,
  closeButtonClassName = '',
  closeButtonStyle,
  footer,
  footerClassName = '',
  footerStyle
}) => {
  const modalRef = useRef<HTMLDivElement>(null)
  const previousActiveElement = useRef<HTMLElement | null>(null)
  const headingId = useId()

  // Gerenciar foco e acessibilidade
  useEffect(() => {
    if (isOpen) {
      // Salvar elemento ativo anterior
      previousActiveElement.current = document.activeElement as HTMLElement
      
      // Focar no modal
      if (modalRef.current) {
        modalRef.current.focus()
      }
      
      // Prevenir scroll do body
      document.body.style.overflow = 'hidden'
    } else {
      // Restaurar scroll do body
      document.body.style.overflow = 'unset'
      
      // Restaurar foco
      if (previousActiveElement.current) {
        previousActiveElement.current.focus()
      }
    }

    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isOpen])

  // Fechar com ESC
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (closeOnEscape && event.key === 'Escape' && isOpen) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen, onClose, closeOnEscape])

  if (!isOpen) return null

  const modalNode = document.body

  const sizeMap = {
    sm: '400px',
    md: '600px',
    lg: '800px',
    xl: '1200px',
    full: '95%'
  }

  const modalWidth = size === 'full' ? sizeMap[size] : `min(90%, ${sizeMap[size]})`

  const modalMarkup = (
    <div 
      className={`fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 ${overlayClassName}`.trim()}
      aria-labelledby={title ? headingId : undefined}
      aria-modal="true"
      role="dialog"
    >
      <div
        ref={modalRef}
        className={`bg-white rounded-2xl shadow-2xl flex flex-col max-h-[90vh] ${contentClassName}`.trim()}
        style={{
          width: modalWidth,
          maxWidth: '100%',
          ...contentStyle
        }}
        onClick={(e) => e.stopPropagation()}
        tabIndex={-1}
      >
        <div className="flex flex-col h-full max-h-[90vh]">
          {/* Header */}
          {title && (
            <div
              className={`flex items-center justify-between p-6 border-b border-gray-100 ${headerClassName}`.trim()}
              style={headerStyle}
            >
              <h2
                id={headingId}
                className={`text-xl font-bold text-gray-900 ${titleClassName}`.trim()}
              >
                {title}
              </h2>
              {showCloseButton && (
                <button
                  onClick={onClose}
                  className={`p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all duration-200 ${closeButtonClassName}`.trim()}
                  aria-label="Fechar modal"
                  style={closeButtonStyle}
                >
                  <svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          )}

          {/* Body */}
          <div className="flex-1 overflow-y-auto min-h-0">
            <div
              className={`p-6 ${bodyClassName}`.trim()}
              style={bodyStyle}
            >
              {children}
            </div>
          </div>

          {/* Footer */}
          {footer && (
            <div
              className={`flex justify-end gap-3 p-6 border-t border-gray-100 ${footerClassName}`.trim()}
              style={footerStyle}
            >
              {footer}
            </div>
          )}
        </div>
      </div>
    </div>
  )

  if (!modalNode) {
    return null
  }

  return createPortal(modalMarkup, modalNode)
}

export default Modal
